/**
 * Local persistence. No server, no account — everything lives in localStorage.
 *
 * A puzzle is stored whole rather than as a seed. Regenerating from a seed would
 * mean re-running the search on every load, and the search is time-boxed, so the
 * same seed can legitimately produce a different board on a slower device.
 */

import {
  CELLS,
  DIFFICULTIES,
  GLYPHS,
  type Difficulty,
  type Glyph,
  type Mark,
  type Puzzle,
} from './types';

const KEY = 'starbattle.v1';

export interface SavedGame {
  puzzle: Puzzle;
  marks: Mark[];
  /**
   * Provenance of the automatic crosses, cell by cell — see `Board` in
   * `game.ts`. Saved with the marks because it is not recoverable from them:
   * which ✕ came from which star is a record of what happened, and a reload
   * that dropped it would leave the player with crosses nothing can take back.
   */
  sources: number[][];
  /** Seconds of play, accumulated across visits. */
  elapsed: number;
  completed: boolean;
}

/** How the game behaves and looks, kept across puzzles rather than per game. */
export interface Settings {
  /** Cross out the eight cells around a star as it is placed. */
  autoCross: boolean;
  glyph: Glyph;
}

export interface Stored {
  difficulty: Difficulty;
  settings: Settings;
  game: SavedGame | null;
  /** Best time in seconds, keyed by difficulty. */
  best: Partial<Record<Difficulty, number>>;
}

export const DEFAULT_DIFFICULTY: Difficulty = 'medium';

/**
 * Auto-crossing starts off.
 *
 * It changes what a tap does, and a returning player who had never been offered
 * the choice would find their board filling itself in. The settings row says it
 * is there.
 */
export const DEFAULT_SETTINGS: Settings = { autoCross: false, glyph: 'star' };

function isPuzzle(value: unknown): value is Puzzle {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Puzzle>;
  return (
    Array.isArray(candidate.regions) &&
    candidate.regions.length === CELLS &&
    Array.isArray(candidate.solution) &&
    DIFFICULTIES.includes(candidate.difficulty as Difficulty)
  );
}

/**
 * The provenance table, checked cell by cell.
 *
 * Anything that is not a list of in-range cell indices becomes an empty list: a
 * dropped entry costs at worst one automatic ✕ the player has to remove by hand,
 * where trusting it would feed a stray index to the code that retracts crosses.
 */
function readSources(value: unknown): number[][] {
  const rows = Array.isArray(value) ? value : [];
  return Array.from({ length: CELLS }, (_, cell) => {
    const row: unknown = rows[cell];
    if (!Array.isArray(row)) return [];
    return row.filter(
      (entry): entry is number => Number.isInteger(entry) && entry >= 0 && entry < CELLS,
    );
  });
}

/**
 * Reads saved state, discarding anything that does not look right.
 *
 * Storage is shared with whatever the browser had before and survives across
 * versions of this code, so every field is checked rather than trusted; a bad
 * record costs the player one puzzle, an unhandled exception costs them the page.
 * That includes saves from when the board size was configurable — a region map of
 * the wrong length simply fails `isPuzzle` and is dropped.
 */
export function load(): Stored {
  const fallback: Stored = {
    difficulty: DEFAULT_DIFFICULTY,
    settings: { ...DEFAULT_SETTINGS },
    game: null,
    best: {},
  };
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return fallback; // private browsing, or storage disabled
  }
  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (DIFFICULTIES.includes(parsed.difficulty as Difficulty)) {
      fallback.difficulty = parsed.difficulty as Difficulty;
    }
    const settings = parsed.settings;
    if (settings && typeof settings === 'object') {
      fallback.settings.autoCross = settings.autoCross === true;
      if (GLYPHS.includes(settings.glyph as Glyph)) {
        fallback.settings.glyph = settings.glyph as Glyph;
      }
    }
    const game = parsed.game;
    if (game && isPuzzle(game.puzzle) && Array.isArray(game.marks) && game.marks.length === CELLS) {
      fallback.game = {
        puzzle: game.puzzle,
        marks: game.marks as Mark[],
        // Saves from before auto-crossing existed have no provenance, and are
        // right not to: every ✕ on such a board was placed by the player.
        sources: readSources(game.sources),
        elapsed: typeof game.elapsed === 'number' ? game.elapsed : 0,
        completed: game.completed === true,
      };
    }
    if (parsed.best && typeof parsed.best === 'object') {
      for (const difficulty of DIFFICULTIES) {
        const value = parsed.best[difficulty];
        if (typeof value === 'number' && Number.isFinite(value)) fallback.best[difficulty] = value;
      }
    }
  } catch {
    return fallback;
  }
  return fallback;
}

export function save(state: Stored): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // A full or disabled store is not worth interrupting play over.
  }
}
